import React, { useState } from 'react';
import {
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField as MuiTextField,
  Button as MuiButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

interface OrderSearchState {
  startDate?: Date;
  endDate?: Date;
  status?: string;
  searchType?: string; // 상세조건 타입: ordererName, receiverName, productName, address 등
  searchValue?: string; // 상세조건 검색어
}

// 상세 검색 타입 목록
const SEARCH_TYPES = [
  { value: 'ordererName', label: '주문자명' },
  { value: 'receiverName', label: '수취인' },
  { value: 'productName', label: '상품명' },
  { value: 'address', label: '주소' },
  { value: 'orderNumber', label: '주문번호' },
  { value: 'phone', label: '연락처' },
];

interface OrderEditState {
  [orderId: string]: {
    trackingNumber?: string;
    shippingCompany?: string;
    status?: string;
  };
}

// 배송사 목록
const SHIPPING_COMPANIES = [
  { value: 'CJ대한통운', label: 'CJ대한통운' },
  { value: '한진택배', label: '한진택배' },
  { value: '롯데택배', label: '롯데택배' },
  { value: '우체국택배', label: '우체국택배' },
  { value: '로젠택배', label: '로젠택배' },
  { value: '경동택배', label: '경동택배' },
  { value: '대신택배', label: '대신택배' },
  { value: '일양로지스', label: '일양로지스' },
  { value: '합동택배', label: '합동택배' },
  { value: 'GS포스트박스', label: 'GS포스트박스' },
];

// 영문 코드 -> 한글 택배사명 변환 (기존 데이터 호환성)
const CARRIER_CODE_TO_NAME: { [key: string]: string } = {
  'CJ': 'CJ대한통운',
  'HANJIN': '한진택배',
  'LOTTE': '롯데택배',
  'POST': '우체국택배',
  'LOGEN': '로젠택배',
  'CU': 'CU편의점택배',
  'GS': 'GS편의점택배',
};

// 택배사 코드를 정규화 (영문 코드면 한글로 변환)
const normalizeCarrierName = (carrier: string | undefined | null): string => {
  if (!carrier) return '';
  return CARRIER_CODE_TO_NAME[carrier] || carrier;
};

// 주문 상태 목록 (DB enum과 일치)
const ORDER_STATUSES = [
  { value: 'PENDING', label: '결제대기', color: 'default' },
  { value: 'PAID', label: '결제완료', color: 'info' },
  { value: 'PROCESSING', label: '상품준비중', color: 'warning' },
  { value: 'SHIPPED', label: '배송중', color: 'primary' },
  { value: 'DELIVERED', label: '배송완료', color: 'success' },
  { value: 'COMPLETED', label: '구매확정', color: 'success' },
  { value: 'CANCELLED', label: '취소', color: 'error' },
  { value: 'REFUNDED', label: '환불', color: 'error' },
];

// 배송 상태 필터 목록 (라디오 버튼용)
const DELIVERY_STATUS_FILTERS = [
  { value: '', label: '전체' },
  { value: 'PAID', label: '결제완료' },
  { value: 'PROCESSING', label: '상품준비중' },
  { value: 'SHIPPING_READY', label: '배송지시' },
  { value: 'SHIPPED', label: '배송중' },
  { value: 'DELIVERED', label: '배송완료' },
  { value: 'DIRECT_DELIVERY', label: '업체 직접 배송' },
];

// 주문 상세 검색 컴포넌트
export const OrderSearch = () => {
  const [searchParams, setSearchParams] = useState<OrderSearchState>({
    searchType: 'ordererName',
  });
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('30days'); // 선택된 기간 버튼
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [orderEdits, setOrderEdits] = useState<OrderEditState>({});

  // 취소 다이얼로그 상태
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancel_reason, setCancelReason] = useState<'customer' | 'seller'>('customer');
  const [cancel_note, setCancelNote] = useState('');
  const [orderToCancel, setOrderToCancel] = useState<string | null>(null);

  // 일괄 적용 상태
  const [bulkShippingCompany, setBulkShippingCompany] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (searchResults.length === 0) {
      notify('다운로드할 데이터가 없습니다', { type: 'warning' });
      return;
    }

    const escapeCSV = (value: string) => {
      const escaped = value.replace(/"/g, '""');
      return `"${escaped}"`;
    };

    const headers = ['주문번호', '주문일시', '회원', '수취인', '연락처', '상품', '금액', '결제', '배송사', '송장번호', '상태'];
    const csvData = searchResults.map(order => [
      order.orderNumber || '-',
      order.created_at ? new Date(order.created_at).toLocaleDateString() : '-',
      order.userName || '-',
      order.recipientName || '-',
      order.recipientPhone || '-',
      order.items?.map((item: any) => item.product_name).join(', ') || '-',
      order.totalAmount?.toLocaleString() || '0',
      order.paymentMethod || '-',
      order.shippingCompany || '-',
      order.trackingNumber || '-',
      order.status || '-',
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...csvData.map(row => row.map(cell => escapeCSV(String(cell))).join(','))
    ].join('\n');

    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `주문검색_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notify('엑셀 다운로드가 완료되었습니다', { type: 'success' });
  };

  // 인쇄
  const handlePrint = () => {
    if (searchResults.length === 0) {
      notify('인쇄할 데이터가 없습니다', { type: 'warning' });
      return;
    }
    window.print();
  };

  // 주문 상세 보기
  const handleViewDetail = (orderId: string) => {
    window.location.href = `/#/orders/${orderId}/show`;
  };

  // 검색 실행
  const handleSearch = async () => {
    setLoading(true);
    try {
      // Date 객체를 ISO 문자열로 변환하여 필터 전달
      const filter: any = {};

      if (searchParams.startDate) {
        filter.startDate = searchParams.startDate.toISOString();
      }
      if (searchParams.endDate) {
        filter.endDate = searchParams.endDate.toISOString();
      }
      if (searchParams.status) {
        filter.status = searchParams.status;
      }
      if (searchParams.searchType && searchParams.searchValue) {
        filter[searchParams.searchType] = searchParams.searchValue;
      }

      console.log('[OrderSearch] 검색 필터:', filter);

      const { data, total } = await dataProvider.getList('orders', {
        filter,
        pagination: { page: page + 1, perPage: rowsPerPage },
        sort: { field: 'created_at', order: 'DESC' },
      });
      setSearchResults(data);
      setTotalCount(total || 0);
      setSelectedOrders([]);
      setOrderEdits({});
      notify(`${total}건의 주문을 찾았습니다`, { type: 'success' });
    } catch (error) {
      console.error('[OrderSearch] 검색 오류:', error);
      notify('검색 중 오류가 발생했습니다', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 검색 초기화
  const handleReset = () => {
    setSearchParams({ searchType: 'ordererName' });
    setSearchResults([]);
    setPage(0);
    setSelectedPeriod('');
    setSelectedOrders([]);
    setOrderEdits({});
    notify('검색 조건이 초기화되었습니다', { type: 'info' });
  };

  // 페이지 변경
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    handleSearch();
  };

  // 페이지당 행 수 변경
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    handleSearch();
  };

  // 전체 선택/해제
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedOrders(searchResults.map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  // 개별 선택
  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // 인라인 편집 값 변경
  const handleEditChange = (orderId: string, field: string, value: string) => {
    setOrderEdits(prev => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [field]: value,
      }
    }));
  };

  // 개별 주문 저장
  const handleSaveOrder = async (orderId: string) => {
    const edits = orderEdits[orderId];
    if (!edits) {
      notify('변경 사항이 없습니다', { type: 'warning' });
      return;
    }

    try {
      // 택배사 코드 정규화 (영문 코드 -> 한글)
      const normalizedShippingCompany = normalizeCarrierName(edits.shippingCompany);

      // 송장번호가 입력된 경우 배송 추적 API 호출
      if (edits.trackingNumber && normalizedShippingCompany) {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const trackingResponse = await fetch(`${baseUrl}/api/v1/orders/tracking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderId,
            trackingNumber: edits.trackingNumber,
            shippingCompany: normalizedShippingCompany,
          }),
        });

        const trackingResult = await trackingResponse.json();

        if (trackingResult.success) {
          // 배송 추적 성공 - 응답에서 업데이트된 주문 정보 사용
          const updatedOrder = trackingResult.data.order;

          setSearchResults(prev => prev.map(order =>
            order.id === orderId ? { ...order, ...updatedOrder } : order
          ));

          setOrderEdits(prev => {
            const newEdits = { ...prev };
            delete newEdits[orderId];
            return newEdits;
          });

          // React Admin 캐시 무효화 (다른 페이지 이동 시 최신 데이터 표시)
          refresh();

          notify(trackingResult.data.message || '배송 정보가 업데이트되었습니다', { type: 'success' });
          return;
        } else {
          console.warn('[Order Search] Tracking API failed, falling back to regular update');
        }
      }

      // 일반 업데이트 (배송 추적 API 미사용 또는 실패 시)
      await dataProvider.update('orders', {
        id: orderId,
        data: edits,
        previousData: searchResults.find(o => o.id === orderId),
      });

      setSearchResults(prev => prev.map(order =>
        order.id === orderId ? { ...order, ...edits } : order
      ));

      setOrderEdits(prev => {
        const newEdits = { ...prev };
        delete newEdits[orderId];
        return newEdits;
      });

      // React Admin 캐시 무효화 (다른 페이지 이동 시 최신 데이터 표시)
      refresh();

      notify('주문이 업데이트되었습니다', { type: 'success' });
    } catch (error) {
      console.error('[Order Search] Save error:', error);
      notify('업데이트 중 오류가 발생했습니다', { type: 'error' });
    }
  };

  // 선택된 주문 일괄 저장
  const handleBulkSave = async () => {
    if (selectedOrders.length === 0) {
      notify('선택된 주문이 없습니다', { type: 'warning' });
      return;
    }

    const updateData: any = {};
    if (bulkShippingCompany) updateData.shipping_company = bulkShippingCompany;
    if (bulkStatus) updateData.status = bulkStatus;

    if (Object.keys(updateData).length === 0) {
      notify('적용할 변경 사항이 없습니다', { type: 'warning' });
      return;
    }

    try {
      // 선택된 모든 주문 업데이트
      await Promise.all(selectedOrders.map(orderId =>
        dataProvider.update('orders', {
          id: orderId,
          data: {
            ...orderEdits[orderId],
            ...updateData,
          },
          previousData: searchResults.find(o => o.id === orderId),
        })
      ));

      // 검색 결과 업데이트
      setSearchResults(prev => prev.map(order =>
        selectedOrders.includes(order.id)
          ? { ...order, ...orderEdits[order.id], ...updateData }
          : order
      ));

      // 상태 초기화
      const updatedCount = selectedOrders.length;
      setSelectedOrders([]);
      setOrderEdits({});
      setBulkShippingCompany('');
      setBulkStatus('');

      // React Admin 캐시 무효화 (다른 페이지 이동 시 최신 데이터 표시)
      refresh();

      notify(`${updatedCount}건의 주문이 업데이트되었습니다`, { type: 'success' });
    } catch (error) {
      notify('일괄 업데이트 중 오류가 발생했습니다', { type: 'error' });
    }
  };

  // 주문 취소 다이얼로그 열기
  const handleOpenCancelDialog = (orderId: string) => {
    setOrderToCancel(orderId);
    setCancelReason('customer');
    setCancelNote('');
    setCancelDialogOpen(true);
  };

  // 선택된 주문 일괄 취소
  const handleBulkCancel = () => {
    if (selectedOrders.length === 0) {
      notify('선택된 주문이 없습니다', { type: 'warning' });
      return;
    }
    setOrderToCancel(null); // null means bulk cancel
    setCancelReason('customer');
    setCancelNote('');
    setCancelDialogOpen(true);
  };

  // 주문 취소 실행
  const handleConfirmCancel = async () => {
    const ordersToCancel = orderToCancel ? [orderToCancel] : selectedOrders;

    if (ordersToCancel.length === 0) {
      notify('취소할 주문이 없습니다', { type: 'warning' });
      return;
    }

    try {
      await Promise.all(ordersToCancel.map(orderId =>
        dataProvider.update('orders', {
          id: orderId,
          data: {
            status: 'CANCELLED',
            cancel_reason: cancel_reason,
            cancel_note: cancel_note,
            cancelled_at: new Date().toISOString(),
          },
          previousData: searchResults.find(o => o.id === orderId),
        })
      ));

      // 검색 결과 업데이트
      setSearchResults(prev => prev.map(order =>
        ordersToCancel.includes(order.id)
          ? { ...order, status: 'CANCELLED', cancel_reason, cancel_note }
          : order
      ));

      const cancelledCount = ordersToCancel.length;
      setCancelDialogOpen(false);
      setOrderToCancel(null);
      setSelectedOrders([]);

      // React Admin 캐시 무효화 (다른 페이지 이동 시 최신 데이터 표시)
      refresh();

      notify(`${cancelledCount}건의 주문이 취소되었습니다`, { type: 'success' });
    } catch (error) {
      notify('주문 취소 중 오류가 발생했습니다', { type: 'error' });
    }
  };

  // 기간 버튼 클릭 핸들러
  const handlePeriodClick = (period: string) => {
    setSelectedPeriod(period);

    // 로컬 타임존의 오늘 날짜 기준
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const date = now.getDate();

    // 오늘의 시작 (00:00:00.000 로컬 시간)
    let startDate = new Date(year, month, date, 0, 0, 0, 0);
    // 오늘의 끝 (23:59:59.999 로컬 시간)
    const endDate = new Date(year, month, date, 23, 59, 59, 999);

    switch (period) {
      case 'today':
        // 오늘만 (startDate는 이미 오늘 00:00:00)
        break;
      case '7days':
        // 7일 전부터 오늘까지
        startDate = new Date(year, month, date - 6, 0, 0, 0, 0);
        break;
      case '30days':
        // 30일 전부터 오늘까지
        startDate = new Date(year, month, date - 29, 0, 0, 0, 0);
        break;
      default:
        return;
    }

    console.log('[OrderSearch] 기간 설정:', {
      period,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });

    setSearchParams(prev => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  // 날짜 직접 변경 시 기간 버튼 선택 해제
  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setSelectedPeriod('');
    setSearchParams(prev => ({
      ...prev,
      [field]: value ? new Date(value) : undefined,
    }));
  };

  // 로컬 날짜를 YYYY-MM-DD 형식으로 포맷
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 상태 라벨 가져오기
  const getStatusLabel = (status: string) => {
    const found = ORDER_STATUSES.find(s => s.value === status);
    return found ? found.label : status;
  };

  // 상태 색상 가져오기
  const getStatusColor = (status: string): any => {
    const found = ORDER_STATUSES.find(s => s.value === status);
    return found ? found.color : 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E' }}>
          주문 검색
        </Typography>
      </Box>

      {/* 검색 필터 - 단순화된 UI */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {/* 기간 */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            py: 2,
            px: 3,
            borderBottom: '1px solid #eee'
          }}>
            <Typography sx={{ width: 100, fontWeight: 500, color: '#666', flexShrink: 0 }}>
              기간
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <MuiButton
                variant={selectedPeriod === 'today' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handlePeriodClick('today')}
                sx={{
                  minWidth: 60,
                  borderColor: selectedPeriod === 'today' ? 'primary.main' : '#ddd',
                  color: selectedPeriod === 'today' ? 'white' : '#333',
                }}
              >
                오늘
              </MuiButton>
              <MuiButton
                variant={selectedPeriod === '7days' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handlePeriodClick('7days')}
                sx={{
                  minWidth: 80,
                  borderColor: selectedPeriod === '7days' ? 'primary.main' : '#ddd',
                  color: selectedPeriod === '7days' ? 'white' : '#333',
                }}
              >
                지난 7일
              </MuiButton>
              <MuiButton
                variant={selectedPeriod === '30days' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handlePeriodClick('30days')}
                sx={{
                  minWidth: 80,
                  borderColor: selectedPeriod === '30days' ? 'primary.main' : '#ddd',
                  color: selectedPeriod === '30days' ? 'white' : '#333',
                }}
              >
                지난 30일
              </MuiButton>
              <MuiTextField
                type="date"
                size="small"
                value={searchParams.startDate ? formatDateForInput(searchParams.startDate) : ''}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                sx={{ width: 160, ml: 1 }}
                InputProps={{
                  sx: { height: 32 }
                }}
              />
              <Typography sx={{ mx: 0.5, color: '#666' }}>~</Typography>
              <MuiTextField
                type="date"
                size="small"
                value={searchParams.endDate ? formatDateForInput(searchParams.endDate) : ''}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                sx={{ width: 160 }}
                InputProps={{
                  sx: { height: 32 }
                }}
              />
            </Box>
          </Box>

          {/* 배송상태 */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            py: 2,
            px: 3,
            borderBottom: '1px solid #eee'
          }}>
            <Typography sx={{ width: 100, fontWeight: 500, color: '#666', flexShrink: 0 }}>
              배송상태
            </Typography>
            <RadioGroup
              row
              value={searchParams.status || ''}
              onChange={(e) => setSearchParams(prev => ({ ...prev, status: e.target.value }))}
              sx={{ gap: 1 }}
            >
              {DELIVERY_STATUS_FILTERS.map(status => (
                <FormControlLabel
                  key={status.value}
                  value={status.value}
                  control={<Radio size="small" />}
                  label={status.label}
                  sx={{
                    mr: 2,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.9rem',
                      color: '#333'
                    }
                  }}
                />
              ))}
            </RadioGroup>
          </Box>

          {/* 상세조건 */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            py: 2,
            px: 3,
          }}>
            <Typography sx={{ width: 100, fontWeight: 500, color: '#666', flexShrink: 0 }}>
              상세조건
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={searchParams.searchType || 'ordererName'}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, searchType: e.target.value }))}
                  sx={{ height: 40 }}
                >
                  {SEARCH_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <MuiTextField
                size="small"
                placeholder="검색어를 입력하세요"
                value={searchParams.searchValue || ''}
                onChange={(e) => setSearchParams(prev => ({ ...prev, searchValue: e.target.value }))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                sx={{ flex: 1, maxWidth: 400 }}
                InputProps={{
                  sx: { height: 40 }
                }}
              />
            </Box>
          </Box>

          {/* 검색 버튼 */}
          <Box sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            py: 2,
            px: 3,
            bgcolor: '#fafafa',
            borderTop: '1px solid #eee'
          }}>
            <MuiButton
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleReset}
              sx={{ minWidth: 100 }}
            >
              초기화
            </MuiButton>
            <MuiButton
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
              sx={{ minWidth: 100 }}
            >
              검색
            </MuiButton>
          </Box>
        </CardContent>
      </Card>

      {/* 검색 결과 */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : searchResults.length > 0 ? (
        <Card>
          <CardContent>
            {/* 상단 툴바 */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                검색 결과 ({totalCount}건) {selectedOrders.length > 0 && `- ${selectedOrders.length}건 선택됨`}
              </Typography>
              <Box display="flex" gap={1}>
                <MuiButton startIcon={<DownloadIcon />} onClick={handleExcelDownload}>
                  엑셀 다운로드
                </MuiButton>
                <MuiButton startIcon={<PrintIcon />} onClick={handlePrint}>
                  인쇄
                </MuiButton>
              </Box>
            </Box>

            {/* 일괄 적용 영역 */}
            {selectedOrders.length > 0 && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  선택된 주문 일괄 처리
                </Typography>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>배송사</InputLabel>
                    <Select
                      value={bulkShippingCompany}
                      label="배송사"
                      onChange={(e) => setBulkShippingCompany(e.target.value)}
                    >
                      <MenuItem value="">선택</MenuItem>
                      {SHIPPING_COMPANIES.map(company => (
                        <MenuItem key={company.value} value={company.value}>{company.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>상태변경</InputLabel>
                    <Select
                      value={bulkStatus}
                      label="상태변경"
                      onChange={(e) => setBulkStatus(e.target.value)}
                    >
                      <MenuItem value="">선택</MenuItem>
                      {ORDER_STATUSES.map(status => (
                        <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MuiButton
                    variant="contained"
                    color="primary"
                    onClick={handleBulkSave}
                    startIcon={<CheckCircleIcon />}
                  >
                    일괄 적용
                  </MuiButton>
                  <MuiButton
                    variant="outlined"
                    color="error"
                    onClick={handleBulkCancel}
                    startIcon={<CancelIcon />}
                  >
                    일괄 취소
                  </MuiButton>
                </Box>
              </Box>
            )}

            {/* 테이블 */}
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedOrders.length > 0 && selectedOrders.length < searchResults.length}
                        checked={searchResults.length > 0 && selectedOrders.length === searchResults.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>주문번호</TableCell>
                    <TableCell>주문일</TableCell>
                    <TableCell>상품명</TableCell>
                    <TableCell>개수</TableCell>
                    <TableCell align="right">판매가</TableCell>
                    <TableCell>수취인 정보</TableCell>
                    <TableCell>주문 상태</TableCell>
                    <TableCell>배송사·송장</TableCell>
                    <TableCell>액션</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map(order => {
                    const edits = orderEdits[order.id] || {};
                    const hasChanges = Object.keys(edits).length > 0;

                    return (
                      <TableRow key={order.id} selected={selectedOrders.includes(order.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedOrders.includes(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {order.orderNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {order.created_at ? new Date(order.created_at).toLocaleString('ko-KR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </TableCell>
                        <TableCell>
                          <Box>
                            {order.items?.map((item: any, index: number) => (
                              <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {item.product_name || '상품명 없음'}
                              </Typography>
                            )) || '-'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {order.items?.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0) || 1}개
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {(order.total_amount || order.final_amount)?.toLocaleString() || '0'}원
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.recipient_name || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.recipient_phone || '-'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={edits.status ?? order.status ?? ''}
                              onChange={(e) => handleEditChange(order.id, 'status', e.target.value)}
                              sx={{
                                '& .MuiSelect-select': {
                                  py: 0.5,
                                }
                              }}
                            >
                              {ORDER_STATUSES.map(status => (
                                <MenuItem key={status.value} value={status.value}>
                                  <Chip
                                    label={status.label}
                                    size="small"
                                    color={status.color as any}
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormControl size="small" sx={{ width: 130 }}>
                              <Select
                                value={edits.shippingCompany ?? normalizeCarrierName(order.shippingCompany) ?? ''}
                                onChange={(e) => handleEditChange(order.id, 'shippingCompany', e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">배송사</MenuItem>
                                {SHIPPING_COMPANIES.map(company => (
                                  <MenuItem key={company.value} value={company.value}>{company.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <MuiTextField
                              size="small"
                              placeholder="송장번호"
                              value={edits.trackingNumber ?? order.trackingNumber ?? ''}
                              onChange={(e) => handleEditChange(order.id, 'trackingNumber', e.target.value)}
                              sx={{ width: 130 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexDirection="column">
                            {hasChanges && (
                              <MuiButton
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleSaveOrder(order.id)}
                                sx={{ fontSize: '0.7rem', py: 0.25 }}
                              >
                                저장
                              </MuiButton>
                            )}
                            <MuiButton
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewDetail(order.id)}
                              sx={{ fontSize: '0.7rem', py: 0.25 }}
                            >
                              상세
                            </MuiButton>
                            {order.status !== 'CANCELLED' && (
                              <MuiButton
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleOpenCancelDialog(order.id)}
                                sx={{ fontSize: '0.7rem', py: 0.25 }}
                              >
                                취소
                              </MuiButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="페이지당 행 수"
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="textSecondary">
                검색 조건을 입력하고 검색 버튼을 클릭하세요
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 주문 취소 다이얼로그 */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          주문 취소
          {orderToCancel === null && selectedOrders.length > 0 && ` (${selectedOrders.length}건)`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              취소 사유 선택
            </Typography>
            <RadioGroup
              value={cancel_reason}
              onChange={(e) => setCancelReason(e.target.value as 'customer' | 'seller')}
            >
              <FormControlLabel
                value="customer"
                control={<Radio />}
                label="고객 사유 (고객 변심, 주문 실수 등)"
              />
              <FormControlLabel
                value="seller"
                control={<Radio />}
                label="판매자 사유 (재고 부족, 배송 불가 등)"
              />
            </RadioGroup>

            <MuiTextField
              fullWidth
              multiline
              rows={3}
              label="취소 메모 (선택사항)"
              value={cancel_note}
              onChange={(e) => setCancelNote(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="취소 사유에 대한 상세 내용을 입력하세요"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setCancelDialogOpen(false)}>
            닫기
          </MuiButton>
          <MuiButton
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            startIcon={<CancelIcon />}
          >
            취소 확정
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
